import { useMemo } from "react";

import type { Pagination } from "../../../../hooks/usePagination.ts";

const paginateSection = <T>(
  section: T[],
  { page, pageSize }: Pagination
): T[] => {
  const startIndex = (page - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  if (startIndex >= section.length) {
    // We've paginated past this section, show 0 results
    return [];
  }
  if (endIndex > section.length) {
    // If we're on the final page for this section, show remaining items
    return section.slice(startIndex);
  }
  return section.slice(startIndex, endIndex);
};

/**
 * Paginates active and pending sections independently with the same page and pageSize.
 * Shows 0 results when pageNumber * pageSize > numResults for a section.
 *
 * @param activeSection - Active section array to paginate
 * @param pendingSection - Pending section array to paginate
 * @param page - Current page number (1-indexed)
 * @param pageSize - Number of items per page
 * @returns Object containing paginated arrays for both sections and total items count
 */
export const useActiveAndPendingPagination = <T>(
  activeSection: T[],
  pendingSection: T[],
  pagination: Pagination
) => {
  const paginatedActiveSection = useMemo(
    () => paginateSection(activeSection, pagination),
    [activeSection, pagination]
  );

  const paginatedPendingSection = useMemo(
    () => paginateSection(pendingSection, pagination),
    [pendingSection, pagination]
  );

  const totalItems = useMemo(
    () => Math.max(activeSection.length, pendingSection.length),
    [activeSection.length, pendingSection.length]
  );

  return {
    paginatedActiveSection,
    paginatedPendingSection,
    totalItems,
  };
};
